import { borderRadius, colors } from '@/theme'
import { TEMPLATE_VARIABLES, type TemplateVariable } from '@/types/template-variables'
import DataObjectIcon from '@mui/icons-material/DataObject'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatClearIcon from '@mui/icons-material/FormatClear'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough'
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined'
import LinkIcon from '@mui/icons-material/Link'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import RedoIcon from '@mui/icons-material/Redo'
import UndoIcon from '@mui/icons-material/Undo'
import {
  Box,
  Divider,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  type SvgIconProps,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import type { Editor } from '@tiptap/core'
import { type useEditor, useEditorState } from '@tiptap/react'
import { useMemo, useState, type ComponentType, type MouseEvent } from 'react'

type ToolbarState = ReturnType<typeof useToolbarState>

interface ToolbarActionDef {
  key: string
  title: string
  icon: ComponentType<SvgIconProps>
  run: (editor: Editor) => void
  isActive?: (state: NonNullable<ToolbarState>) => boolean
  isDisabled?: (state: NonNullable<ToolbarState>) => boolean
}

// Simple, always-available formatting commands — anything needing its own UI
// (link prompt, block-style dropdown, variable picker) is defined separately below.
const TOOLBAR_GROUPS: ToolbarActionDef[][] = [
  [
    {
      key: 'undo',
      title: 'Undo',
      icon: UndoIcon,
      run: e => e.chain().focus().undo().run(),
      isDisabled: s => !s.canUndo
    },
    {
      key: 'redo',
      title: 'Redo',
      icon: RedoIcon,
      run: e => e.chain().focus().redo().run(),
      isDisabled: s => !s.canRedo
    }
  ],
  [
    {
      key: 'bold',
      title: 'Bold',
      icon: FormatBoldIcon,
      run: e => e.chain().focus().toggleBold().run(),
      isActive: s => s.bold
    },
    {
      key: 'italic',
      title: 'Italic',
      icon: FormatItalicIcon,
      run: e => e.chain().focus().toggleItalic().run(),
      isActive: s => s.italic
    },
    {
      key: 'underline',
      title: 'Underline',
      icon: FormatUnderlinedIcon,
      run: e => e.chain().focus().toggleUnderline().run(),
      isActive: s => s.underline
    },
    {
      key: 'strike',
      title: 'Strikethrough',
      icon: FormatStrikethroughIcon,
      run: e => e.chain().focus().toggleStrike().run(),
      isActive: s => s.strike
    }
  ],
  [
    {
      key: 'orderedList',
      title: 'Numbered list',
      icon: FormatListNumberedIcon,
      run: e => e.chain().focus().toggleOrderedList().run(),
      isActive: s => s.orderedList
    },
    {
      key: 'bulletList',
      title: 'Bullet list',
      icon: FormatListBulletedIcon,
      run: e => e.chain().focus().toggleBulletList().run(),
      isActive: s => s.bulletList
    }
  ]
]

const BLOCK_STYLE_OPTIONS = [
  { value: 'paragraph', label: 'Paragraph' },
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
  { value: 'blockquote', label: 'Quote' }
] as const

function useToolbarState(editor: ReturnType<typeof useEditor>) {
  return useEditorState({
    editor,
    selector: ({ editor }) =>
      editor
        ? {
            bold: editor.isActive('bold'),
            italic: editor.isActive('italic'),
            underline: editor.isActive('underline'),
            strike: editor.isActive('strike'),
            bulletList: editor.isActive('bulletList'),
            orderedList: editor.isActive('orderedList'),
            link: editor.isActive('link'),
            blockStyle: editor.isActive('heading', { level: 1 })
              ? 'h1'
              : editor.isActive('heading', { level: 2 })
                ? 'h2'
                : editor.isActive('heading', { level: 3 })
                  ? 'h3'
                  : editor.isActive('blockquote')
                    ? 'blockquote'
                    : 'paragraph',
            canUndo: editor.can().undo(),
            canRedo: editor.can().redo()
          }
        : null
  })
}

function ToolbarButton({
  title,
  active,
  disabled,
  onClick,
  children
}: {
  title: string
  active?: boolean
  disabled?: boolean
  onClick: (event: MouseEvent<HTMLButtonElement>) => void
  children: React.ReactNode
}) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size='small'
          disabled={disabled}
          onMouseDown={e => e.preventDefault()}
          onClick={onClick}
          sx={{
            borderRadius: borderRadius.xs,
            color: active ? colors.brand.primary : colors.text.secondary,
            bgcolor: active ? colors.brand.primaryLight : 'transparent'
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  )
}

function BlockStyleSelect({ editor, value }: { editor: Editor; value: string }) {
  return (
    <TextField
      select
      size='small'
      value={value}
      onMouseDown={e => e.stopPropagation()}
      onChange={e => {
        const next = e.target.value
        const chain = editor.chain().focus()
        if (next === 'paragraph') chain.setParagraph().run()
        else if (next === 'blockquote') chain.setBlockquote().run()
        else chain.toggleHeading({ level: Number(next.slice(1)) as 1 | 2 | 3 }).run()
      }}
      sx={{ minWidth: 130, '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
    >
      {BLOCK_STYLE_OPTIONS.map(option => (
        <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  )
}

function VariableMenuButton({ editor }: { editor: Editor }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const variablesByGroup = useMemo(
    () =>
      TEMPLATE_VARIABLES.reduce(
        (acc, variable) => {
          if (!acc[variable.group]) acc[variable.group] = []
          acc[variable.group].push(variable)
          return acc
        },
        {} as Record<string, TemplateVariable[]>
      ),
    []
  )

  const handleInsert = (key: string) => {
    setAnchorEl(null)
    // Deferred: see the matching comment on variableSuggestion.command in
    // variableNode.tsx — inserting from a MenuItem's onClick synchronously
    // trips a flushSync-during-render error from the node's ReactRenderer.
    setTimeout(() => {
      editor.chain().focus().insertContent({ type: 'variable', attrs: { key } }).run()
    })
  }

  return (
    <>
      <ToolbarButton title='Insert variable' onClick={event => setAnchorEl(event.currentTarget)}>
        <DataObjectIcon sx={{ fontSize: 18 }} />
      </ToolbarButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: { sx: { minWidth: 220, maxHeight: 320, mt: 0.5, '& .MuiMenuItem-root': { fontSize: 13, py: 0.75 } } }
        }}
      >
        {Object.entries(variablesByGroup).flatMap(([group, variables]) => [
          <ListSubheader
            key={`group-${group}`}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              lineHeight: '28px',
              textTransform: 'uppercase',
              color: colors.text.muted,
              letterSpacing: '0.5px'
            }}
          >
            {group}
          </ListSubheader>,
          ...variables.map(variable => (
            <MenuItem key={variable.key} onClick={() => handleInsert(variable.key)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography sx={{ fontSize: 13 }}>{variable.label}</Typography>
                <Typography sx={{ fontSize: 11, color: colors.text.muted, ml: 'auto' }}>
                  {`{{${variable.key}}}`}
                </Typography>
              </Box>
            </MenuItem>
          ))
        ])}
      </Menu>
    </>
  )
}

export function Toolbar({ editor, showVariables }: { editor: Editor; showVariables: boolean }) {
  const state = useToolbarState(editor)
  if (!state) return null

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('URL', previousUrl ?? '')
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.25,
        p: 0.5,
        borderBottom: `1px solid ${colors.border.default}`,
        bgcolor: colors.background.surface
      }}
    >
      {TOOLBAR_GROUPS.map((group, index) => (
        <Box key={group[0].key} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          {index > 0 && <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 1 }} />}
          {group.map(action => (
            <ToolbarButton
              key={action.key}
              title={action.title}
              active={action.isActive?.(state)}
              disabled={action.isDisabled?.(state)}
              onClick={() => action.run(editor)}
            >
              <action.icon sx={{ fontSize: 18 }} />
            </ToolbarButton>
          ))}
        </Box>
      ))}
      <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 1 }} />
      <ToolbarButton title={state.link ? 'Remove link' : 'Insert link'} active={state.link} onClick={setLink}>
        {state.link ? <LinkOffIcon sx={{ fontSize: 18 }} /> : <LinkIcon sx={{ fontSize: 18 }} />}
      </ToolbarButton>
      <ToolbarButton title='Clear formatting' onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
        <FormatClearIcon sx={{ fontSize: 18 }} />
      </ToolbarButton>
      <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 1 }} />
      <BlockStyleSelect editor={editor} value={state.blockStyle} />
      {showVariables && (
        <>
          <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 1 }} />
          <VariableMenuButton editor={editor} />
        </>
      )}
    </Box>
  )
}
